import * as React from "react";
import TeamsBaseComponent, {ITeamsBaseComponentState} from "msteams-react-base-component";
import {TextField} from "office-ui-fabric-react/lib/TextField";
import {
    PaginatedFilingLocations,
    FilingLocation,
    DmsSearch,
    DmsCaseInfo,
    MetadataFields,
    MetadataTypes,
    MetadataLookupSearchResponse,
    DefaultValues,
    CheckoutFolderInfo,
    EllipsisOptions,
    DmsStorageType,
} from "../models";
import * as microsoftTeams from "@microsoft/teams-js";
import {Icon} from "office-ui-fabric-react/lib/Icon";
import "./styles.css";
import {deleteRequest, post} from "../api";
import LocationItemsListTabView from "./LocationItemsListTabView";
import {
    Checkbox,
    ChoiceGroup,
    CommandBar,
    Dropdown,
    IButtonProps,
    IChoiceGroupOption,
    ICommandBarItemProps,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
} from "office-ui-fabric-react";
import GetIcons from "./GetIcons";
import {TeamsTab} from "./TeamsTab";
import {TeamsDocumentStatusTabDateMetadata} from "./statustab/metadata/TeamsDocumentStatusTabDateMetadata";
import {TeamsDocumentStatusTabLookupMetadata} from "./statustab/metadata/TeamsDocumentStatusTabLookupMetadata";
import {TeamsDocumentStatusTabSearchMetadata} from "./statustab/metadata/TeamsDocumentStatusTabSearchMetadata";
import {TeamsDocumentStatusTabTextMetadata} from "./statustab/metadata/TeamsDocumentStatusTabTextMetadata";
import {ReactNode} from "react";
import {DarkModeCalloutStyles} from "./DarkModeCalloutStyles";
import {TeamsApiService} from "../services/teams/TeamsApiService";
import {TeamsDocumentStatusTabBanner} from "./statustab/banners/TeamsDocumentStatusTabBanner";
import {ApiCallState, NetworkState} from "./ApiCallState";
import {ExternalTeamChannelsSharing} from "./ExternalTeamChannelsSharing";
import {TeamsDocumentsContext} from "./components/TeamsDocuments";
import {BrowseDocumentsContext} from "./components/BrowseDmsDocuments";
import {NewFileNameTextField} from "./NewFileNameTextField";

export interface ITeamsDocumentStatusTabViewState extends ITeamsBaseComponentState {
    searchPhrase: string;
    recentLocations: PaginatedFilingLocations;
    suggestions: FilingLocation[];
    isSearching: boolean;
    hasSearchResults: boolean;
    expandedResults: FilingLocation[];
    isExpanded: boolean;
    expandedParent: FilingLocation;
    deleteEmail: boolean;
    recentLocationsExpanded: boolean;
    folderSearch: boolean;
    teamsId: string;
    userId: string;
    tenantId: string;
    TabView: boolean;
    showCheckinPanel: boolean;
    CheckinDocumentInfo: DmsSearch;
    CheckinComment: string;
    KeepFileCheckedOut: boolean;
    ReplaceVersion: boolean;
    SavingFile: boolean;
    SavedSuccessfully: boolean;
    SaveFailed: boolean;
    RemoveFileState: NetworkState;
    SaveFailedMessage: {
        message: string;
        error:
            | "FileInUseException"
            | "DocumentMissingCheckoutException"
            | "AlreadyUploadedException"
            | "InvalidPermissions"
            | "UploadInProgressException";
    };
    NoLocalChanges: boolean;
    showBanner: boolean;
    spVersioningEnabled: boolean;
    browsingUrl: string;
    validationErrors: string[];
    SearchResultsOpen: boolean;
    MetadataSearchResults: string[];
    LoadingExistingMetadata: boolean;
    reorderMetadata: boolean;
    searchingProfileAttribute: string;
    changingChannel: boolean;
    showSharePanel: boolean;
    showShareButton: boolean;
    selectedFolder: FilingLocation;
    sharingDocument: boolean;
    shareSuccessful: boolean;
    shareFailed: boolean;
    showCheckInBannerWhenSharing: boolean;
    showCheckingOutToPrivateChannelBanner: boolean;
    checkingOutToPrivateChannel: boolean;
    defaultCheckinKey: string;
    externalFileParentFolderId: string;
    showRemoveBanner: boolean;
    showUploadNewDocumentPanel: boolean;
    newFileName: string;
    isNewFileNameError: boolean;
}

export interface ITeamsDocumentStatusTabViewProps {
    CaseInfo: DmsCaseInfo;
    darkMode: boolean;
    teamName: string;
    teamChannels: IDropdownOption[];
    channelId: string;
    teamChannelsRetrievalState: ApiCallState;
    externalTeamChannels: PaginatedFilingLocations;
    externalTeamName: string;
    clientCollabLicensed: boolean;
    externalChannelsRetrievalState: ApiCallState;
    externalTeamNameRetrievalState: ApiCallState;
    isLitigationHoldEnabled: boolean;
    isFrictionlessSpExperienceEnabled: boolean;
    litigationHoldError: string;
}

export interface ITeamsDocumentStatusTabViewWithContextProps extends ITeamsDocumentStatusTabViewProps {
    teamDocumentsContext: React.ContextType<typeof TeamsDocumentsContext>;
    browseDocumentsContext: React.ContextType<typeof BrowseDocumentsContext>;
}

const spCheckinOptions: IChoiceGroupOption[] = [
    {key: "1", text: "Save as major version"},
    {key: "2", text: "Save as minor version"},
];

const checkinOptions: IChoiceGroupOption[] = [
    {key: "1", text: "Save as new version"},
    {key: "2", text: "Replace existing version"},
];

const WithTeamsDocumentStatusTabView =
    (Component: React.ComponentType<ITeamsDocumentStatusTabViewWithContextProps>) =>
    (props: ITeamsDocumentStatusTabViewProps) =>
        (
            // TeamsDocumentsStatusTabView consumes two different contexts so must be passed through as props instead of the usual context.XYZ
            <BrowseDocumentsContext.Consumer>
                {(browseDocumentsContext) => (
                    <TeamsDocumentsContext.Consumer>
                        {(teamDocumentsContext) => (
                            <Component
                                browseDocumentsContext={browseDocumentsContext}
                                teamDocumentsContext={teamDocumentsContext}
                                {...props}
                            />
                        )}
                    </TeamsDocumentsContext.Consumer>
                )}
            </BrowseDocumentsContext.Consumer>
        );

class TeamsDocumentStatusTabViewComponent extends TeamsBaseComponent<
    ITeamsDocumentStatusTabViewWithContextProps,
    ITeamsDocumentStatusTabViewState
> {
    teamsApiService = new TeamsApiService();
    constructor(props: ITeamsDocumentStatusTabViewWithContextProps) {
        super(props);
        this.state = {
            searchPhrase: "",
            recentLocations: {Page: [], PagingToken: null, Errors: null},
            suggestions: [],
            isSearching: false,
            hasSearchResults: false,
            isExpanded: false,
            expandedResults: [],
            expandedParent: null,
            deleteEmail: false,
            recentLocationsExpanded: false,
            theme: null,
            folderSearch: false,
            teamsId: this.getQueryVariable("teamsId"),
            userId: this.getQueryVariable("userId"),
            tenantId: this.getQueryVariable("tenantId"),
            TabView: JSON.parse(this.getQueryVariable("tabView")),
            showCheckinPanel: false,
            CheckinDocumentInfo: null,
            CheckinComment: "",
            KeepFileCheckedOut: false,
            ReplaceVersion: false,
            browsingUrl: "",
            SavingFile: false,
            SavedSuccessfully: false,
            SaveFailed: false,
            SaveFailedMessage: {message: null, error: null},
            RemoveFileState: NetworkState.Idle,
            NoLocalChanges: false,
            showBanner: true,
            spVersioningEnabled: false,
            validationErrors: [],
            SearchResultsOpen: false,
            MetadataSearchResults: [],
            LoadingExistingMetadata: true,
            reorderMetadata: false,
            searchingProfileAttribute: "",
            changingChannel: false,
            showSharePanel: false,
            showShareButton: false,
            selectedFolder: null,
            sharingDocument: false,
            shareSuccessful: false,
            shareFailed: false,
            showCheckInBannerWhenSharing: false,
            showCheckingOutToPrivateChannelBanner: false,
            checkingOutToPrivateChannel: false,
            defaultCheckinKey: "1",
            externalFileParentFolderId: "",
            showRemoveBanner: false,
            showUploadNewDocumentPanel: false,
            newFileName: "",
            isNewFileNameError: false,
        };
    }

    public async componentWillMount(): Promise<void> {
        microsoftTeams.initialize();
    }

    componentDidMount() {
        window.localStorage.setItem("ContentConnectorViewKey", "teamsfilestab");
        if (this.props.CaseInfo.StorageType == DmsStorageType.SharePoint) {
            this.isSpVersioningEnabled();
        }

        this.getCheckinDefaultOption();
        this.props.teamDocumentsContext.fetchRoot();
        this.props.browseDocumentsContext.setParentState({caseInfo: this.props.CaseInfo});
    }

    getCheckinDefaultOption() {
        const tabViewKey = window.localStorage.getItem("DefaultCheckinKey");
        if (tabViewKey) {
            this.setState({defaultCheckinKey: tabViewKey});
            if (tabViewKey == "2") {
                this.setState({ReplaceVersion: true});
            }
        }
    }

    isSpVersioningEnabled() {
        post("/api/teamsdmsintegration/SpVersioningEnabled", {
            TeamsId: this.state.teamsId,
            TenantId: this.state.tenantId,
            CaseDetails: this.props.CaseInfo.CaseDetails,
        })
            .then((response) => response.json())
            .then((response) => {
                const spVersioning = response as boolean;

                this.setState({spVersioningEnabled: spVersioning});
            });
    }
    
    refreshTeamsDocumentsPanel() {
        if (this.state.browsingUrl != "") {
            this.refreshDmsDocuments();
        } else {
            this.props.teamDocumentsContext.refreshTeamsChannelDocuments();
        }
    }

    showCheckinPanel = async (documentInfo: DmsSearch, sharePanel: boolean) => {
        this.setState({
            showCheckinPanel: !sharePanel,
            showSharePanel: sharePanel,
            CheckinDocumentInfo: documentInfo,
            SavedSuccessfully: false,
            SaveFailed: false,
            SaveFailedMessage: {message: null, error: null},
            KeepFileCheckedOut: false,
            CheckinComment: "",
            showCheckingOutToPrivateChannelBanner: true,
        });
        if (documentInfo.Metadata["InTeams"] == "Yes") {
            this.setState({NoLocalChanges: true});
        } else {
            this.setState({NoLocalChanges: false});
        }

        if (documentInfo.Metadata["InDMS"] == "Yes" && this.props.teamDocumentsContext.metadataEnabled) {
            await post("/api/teamsdmsintegration/GetExistingMetadataValues", {
                TeamsId: this.state.teamsId,
                TenantId: this.state.tenantId,
                MetadataFields: this.props.teamDocumentsContext.metadataFields,
                CaseDetails: this.props.CaseInfo.CaseDetails,
                StorageType: this.props.CaseInfo.StorageType,
                DmsFileId: documentInfo.Metadata["DmsFileId"],
            })
                .then((response) => response.json())
                .then((response) => {
                    const metadataFields = response as MetadataFields[];

                    this.setState({LoadingExistingMetadata: false});
                    this.props.teamDocumentsContext.setParentState({metadataFields: metadataFields});
                });
        } else {
            this.setState({LoadingExistingMetadata: false});
        }
    };

    uploadNewDocOrUnlock = async (documentInfo: DmsSearch, option: EllipsisOptions) => {
        if (option == EllipsisOptions.NewDocument) {
            this.setState({
                showUploadNewDocumentPanel: true,
                CheckinDocumentInfo: documentInfo,
                SavedSuccessfully: false,
                SaveFailed: false,
                SaveFailedMessage: {message: null, error: null},
                KeepFileCheckedOut: false,
                CheckinComment: "",
                newFileName: documentInfo.DocName,
            });
        } else {
            this.setState({CheckinDocumentInfo: documentInfo}, () => {
                this.unlockDocument();
            });
        }
    };

    async uploadNewDocument() {
        this.setState({SavingFile: true, SaveFailed: false});

        await post("/api/teamsdmsintegration/UploadNewDocument", {
            TeamsId: this.state.teamsId,
            TenantId: this.state.tenantId,
            Metadata: this.state.CheckinDocumentInfo.Metadata,
            NewFileName: this.state.newFileName,
            Comment: this.state.CheckinComment,
        }).then((r) => {
            if (r.status.toString().startsWith("2")) {
                this.setState({
                    RemoveFileState: NetworkState.Success,
                    SavingFile: false,
                    SavedSuccessfully: true,
                    showBanner: true,
                });

                this.refreshTeamsDocumentsPanel();
            } else {
                this.setState({
                    RemoveFileState: NetworkState.Error,
                    SavingFile: false,
                    SaveFailed: true,
                    SaveFailedMessage: {message: null, error: null},
                });
            }
        });
    }

    async unlockDocument() {
        this.setState({SavingFile: true, SaveFailed: false});

        await post("/api/teamsdmsintegration/UnlockDocument", {
            TeamsId: this.state.teamsId,
            TenantId: this.state.tenantId,
            Metadata: this.state.CheckinDocumentInfo.Metadata,
            NewFileName: this.state.newFileName,
            Comment: this.state.CheckinComment,
        }).then((r) => {
            if (r.status.toString().startsWith("2")) {
                this.setState({
                    RemoveFileState: NetworkState.Success,
                    SavingFile: false,
                    SavedSuccessfully: true,
                    showBanner: true,
                });

                this.refreshTeamsDocumentsPanel();
            } else {
                this.setState({
                    RemoveFileState: NetworkState.Error,
                    SavingFile: false,
                    SaveFailed: true,
                    SaveFailedMessage: {message: null, error: null},
                });
            }
        });
    }

    private refreshDmsDocuments() {
        const linkedLocation = JSON.parse(this.props.CaseInfo.CaseDetails);

        const metadata = {
            LocalFilesBrowse: true,
            StorageType: this.props.CaseInfo.StorageType,
            DmsSiteUrl: linkedLocation.SiteUrl,
            UserId: this.state.userId,
            TenantId: this.state.tenantId,
            LinkedMatter: this.state.CheckinDocumentInfo.Metadata["LinkedMatter"],
        };

        this.props.browseDocumentsContext.browseDms({
            Path: this.state.browsingUrl,
            StorageType: DmsStorageType.SharePoint,
            TeamsId: this.state.teamsId,
            TenantId: this.state.tenantId,
            UserId: this.state.userId,
            ChannelId: this.props.teamDocumentsContext.channelId,
            Metadata: metadata,
            RootUrl: linkedLocation.SiteUrl,
            SpHostKVPrefix: linkedLocation.SpHostKVPrefix,
            NextPage: null,
            SortProperties: null,
            handleResult: (r) => {
                const expandedResults = r as FilingLocation[];
                this.setState({
                    expandedResults: expandedResults,
                });
            },
            handleError: (_error) => {},
            RefreshingView: !this.state.KeepFileCheckedOut,
        });
    }

    resetMetadata() {
        const allMetadata = this.props.teamDocumentsContext.metadataFields;

        if (allMetadata != null) {
            allMetadata.forEach((metadata) => {
                metadata.ManuallyDisabled = false;

                if (metadata.Type == MetadataTypes.Search) {
                    if (!metadata.HasAllOptions) {
                        metadata.DropDownOptions = [];
                    } else {
                        for (const option of metadata.DropDownOptions) {
                            option.selected = false;
                        }
                    }
                    if (metadata.Linked) {
                        metadata.Disabled = true;
                    }
                }

                metadata.Value = "";
                metadata.ErrorMessage = "";
            });
        }

        this.setState({
            showCheckinPanel: false,
            LoadingExistingMetadata: true,
            reorderMetadata: false,
            showSharePanel: false,
            shareSuccessful: false,
            showRemoveBanner: false,
            RemoveFileState: NetworkState.Idle,
        });
        this.props.teamDocumentsContext.setParentState({metadataFields: allMetadata});
        if (
            this.props.teamDocumentsContext.isTeamOwner &&
            this.props.teamDocumentsContext.originalMetadataOrder != this.props.teamDocumentsContext.metadataOrder &&
            this.props.teamDocumentsContext.metadataEnabled
        ) {
            // if the order of the metadata hasn't changed since loading the page, don't try to save it again

            post("/api/teamsdmsintegration/SaveMetadataOrder", {
                MetadataOrder: this.props.teamDocumentsContext.metadataOrder,
                CaseDetails: this.props.CaseInfo.CaseDetails,
                StorageType: this.props.CaseInfo.StorageType,
                TenantId: this.state.tenantId,
                TeamsId: this.state.teamsId,
            });

            const updatedMetadataOrder = {...this.props.teamDocumentsContext.metadataOrder};

            this.props.teamDocumentsContext.setParentState({originalMetadataOrder: updatedMetadataOrder});
        }
    }

    validateMetadata() {
        const errors = [];

        for (const fieldOption of this.props.teamDocumentsContext.metadataFields) {
            if (fieldOption.Required) {
                if (fieldOption.Type == MetadataTypes.Lookup || fieldOption.Type == MetadataTypes.Search) {
                    let valueSelected = false;
                    for (const option of fieldOption.DropDownOptions) {
                        // todo: add check if options aren't null?
                        if (option.selected) {
                            valueSelected = true;
                            break;
                        }
                    }
                    if (!valueSelected) {
                        errors.push(fieldOption.Field + " is required");
                        fieldOption.ErrorMessage = fieldOption.Field + " is required";
                        this.props.teamDocumentsContext.setParentState({
                            metadataFields: this.props.teamDocumentsContext.metadataFields,
                        });
                    }
                } else {
                    if (!fieldOption.Value) {
                        errors.push(fieldOption.Field + " is required");
                        fieldOption.ErrorMessage = fieldOption.Field + " is required";
                        this.props.teamDocumentsContext.setParentState({
                            metadataFields: this.props.teamDocumentsContext.metadataFields,
                        });
                    }
                }
            }
        }

        this.setState({validationErrors: errors});

        return errors.length === 0;
    }

    removeTeamsFile = async () => {
        this.setState({RemoveFileState: NetworkState.Loading});
        await deleteRequest(
            `/api/teamsdmsintegration/RecycleSharepointFile?tenantId=${this.state.tenantId}&teamsId=${this.state.teamsId}&fileId=${this.state.CheckinDocumentInfo.Metadata["SPFileId"]}&channelId=${this.props.teamDocumentsContext.channelId}`
        ).then((r) => {
            if (r.status.toString().startsWith("2")) {
                this.setState({
                    RemoveFileState: NetworkState.Success,
                    showRemoveBanner: true,
                    showCheckInBannerWhenSharing: false,
                });

                this.refreshTeamsDocumentsPanel();
            } else {
                this.setState({RemoveFileState: NetworkState.Error, showRemoveBanner: true});
            }
        });
    };

    saveFileToDms = async (fileDataOverride = {}, useExternalParentFolderId = false) => {
        let canSave = true;
        if (
            this.props.teamDocumentsContext.metadataEnabled &&
            !this.state.showSharePanel &&
            !this.props.isLitigationHoldEnabled
        ) {
            canSave = this.validateMetadata();
        }

        if (useExternalParentFolderId) {
            const externalFolderIdResponse = await post("/api/teamsdmsintegration/GetExternalFileParentFolder", {
                FileId: this.state.CheckinDocumentInfo.Metadata["DmsFileId"],
                TenantId: this.state.tenantId,
                TeamsId: this.state.teamsId,
                SpHostKvPrefix: this.state.CheckinDocumentInfo.Metadata["SpHostKVPrefix"],
                StorageType: this.state.CheckinDocumentInfo.StorageType,
                CaseDetails: this.props.CaseInfo.CaseDetails,
            });

            const externalFolderId = await externalFolderIdResponse.json();
            this.setState({externalFileParentFolderId: externalFolderId});
        }

        if (!canSave) {
            return;
        }

        this.setState({SavingFile: true, SaveFailed: false});

        await post("/api/teamsdmsintegration/SaveFileToDms", {
            Url: this.state.CheckinDocumentInfo.Url,
            Metadata: this.state.CheckinDocumentInfo.Metadata,
            TeamsId: this.state.teamsId,
            ChannelId: this.props.teamDocumentsContext.channelId,
            Comment: this.state.CheckinComment,
            KeepFileInTeams: this.state.KeepFileCheckedOut,
            KeepFileCheckedOut: this.state.KeepFileCheckedOut,
            ReplaceVersion: this.state.ReplaceVersion,
            UseServiceAppToken: false,
            UserId: this.state.userId,
            TenantId: this.state.tenantId,
            MetadataFields: this.props.teamDocumentsContext.metadataFields,
            ExternalFileParentFolderId: this.state.externalFileParentFolderId,
            LitigationHoldEnabled: this.props.isLitigationHoldEnabled,
            ...fileDataOverride,
        })
            .then((r) => {
                if (r.status.toString().startsWith("2") || r.status.toString().startsWith("409")) {
                    return r.json();
                } else {
                    this.setState({
                        SaveFailed: true,
                        SaveFailedMessage: {message: null, error: null},
                        SavingFile: false,
                        showBanner: true,
                        showCheckInBannerWhenSharing: true,
                    });
                }
                throw new Error("Saving file failed");
            })
            .then((j) => {
                if (
                    j.Error === "FileInUseException" ||
                    j.Error === "DocumentMissingCheckoutException" ||
                    j.Error === "AlreadyUploadedException" ||
                    j.Error === "InvalidPermissions" ||
                    j.Error === "UploadInProgressException"
                ) {
                    const msg = this.getCheckOutErrorText(j);
                    this.setState({
                        SaveFailed: true,
                        SaveFailedMessage: {message: msg, error: j.Error},
                        SavingFile: false,
                        showBanner: true,
                        showCheckInBannerWhenSharing: true,
                    });

                    if (j.Error === "UploadInProgressException") {
                        const documents = this.props.teamDocumentsContext.documents;
                        const doc = documents.find(
                            (doc) => doc.Metadata.DmsFileId === this.state.CheckinDocumentInfo.Metadata.DmsFileId
                        );
                        doc.IsUploading = true;

                        const checkInDocument = this.state.CheckinDocumentInfo;
                        checkInDocument.IsUploading = true;
                        this.setState({CheckinDocumentInfo: checkInDocument});
                    }
                    throw new Error(msg);
                } else {
                    const metadata = this.props.teamDocumentsContext.metadataFields;

                    for (const option of metadata) {
                        option.ManuallyDisabled = true;
                    }

                    this.setState({
                        SavingFile: false,
                        SavedSuccessfully: true,
                        showBanner: true,
                        showCheckInBannerWhenSharing: false,
                    });
                    this.props.teamDocumentsContext.setParentState({metadataFields: metadata});
                    this.refreshTeamsDocumentsPanel();
                }
            });
    };

    private timer: NodeJS.Timeout = null;

    runSearchForMetadata = (val: string, metadata: MetadataFields) => {
        if (val == undefined || metadata.HasAllOptions) {
            // this can get called multiple times e.g. on hover, but will have an undefined value. If that's the case return.
            return;
        }

        const items = this.props.teamDocumentsContext.metadataFields;

        clearTimeout(this.timer);

        this.timer = setTimeout(() => {
            const item = items.filter((i) => i == metadata);

            if (!val) {
                if (item[0].DropDownOptions.length != 0) {
                    item[0].DropDownOptions = [];
                    this.props.teamDocumentsContext.setParentState({metadataFields: items}); // return on callback, or is this fine?
                }
                return;
            }

            this.setState({searchingProfileAttribute: metadata.Id});

            post("/api/teamsdmsintegration/MetadataLookupSearch", {
                TenantId: this.state.tenantId,
                TeamsId: this.state.teamsId,
                CaseDetails: this.props.CaseInfo.CaseDetails,
                StorageType: this.props.CaseInfo.StorageType,
                SearchString: val,
                AttributeId: metadata.Id,
                ParentKey: metadata.ParentKey,
                UserSearch: true,
            })
                .then((response) => response.json())
                .then((response) => {
                    const lookupResponse = response as MetadataLookupSearchResponse;

                    item[0].DropDownOptions = lookupResponse.DropDownOptions;
                    item[0].DefaultValuesList = lookupResponse.DefaultValuesList;

                    this.setState({searchingProfileAttribute: ""});
                    this.props.teamDocumentsContext.setParentState({metadataFields: items});
                });
        }, 600);
    };

    getCheckOutErrorText = (j: {Error: string; InUseUsers: string}) => {
        if (j.Error === "FileInUseException") {
            return "File is not available for editing due to background processes. Wait briefly, then try again. Ensure no other user is editing.";
        } else if (j.Error === "DocumentMissingCheckoutException") {
            return (
                "Unable to check in as this document is no longer checked out in " +
                TeamsTab.getStorageTypeLabel(this.props.CaseInfo.StorageType) +
                " to Microsoft Teams."
            );
        } else if (j.Error === "InvalidPermissions") {
            return "Upload failed. Invalid permissions or the destination folder was not found.";
        } else if (j.Error === "UploadInProgressException") {
            return "An upload is already in progress for this file. Please try reloading Content Connector shortly.";
        } else {
            return "Document changed since last check out.";
        }
    };

    setUploadOption = (val: IChoiceGroupOption) => {
        if (val.key == "1") {
            // For sharepoint, reuse the replace version bool. If it's true, upload as a minor version if it's false upload as a major version
            window.localStorage.setItem("DefaultCheckinKey", "1");
            this.setState({ReplaceVersion: false, defaultCheckinKey: "1"});
        } else {
            window.localStorage.setItem("DefaultCheckinKey", "2");
            this.setState({ReplaceVersion: true, defaultCheckinKey: "2"});
        }
    };

    getCheckinOptions() {
        if (this.props.CaseInfo.StorageType == DmsStorageType.SharePoint) {
            if (this.state.spVersioningEnabled) {
                return (
                    <ChoiceGroup
                        style={{
                            backgroundColor: this.props.darkMode ? "#1e1e1e" : "white",
                            color: this.props.darkMode ? "white" : "black",
                        }}
                        disabled={this.state.SavedSuccessfully}
                        defaultSelectedKey={this.state.defaultCheckinKey}
                        options={spCheckinOptions}
                        onChange={(_, val) => this.setUploadOption(val)}
                        required={true}
                    />
                );
            }
        } else {
            return (
                <ChoiceGroup
                    style={{
                        backgroundColor: this.props.darkMode ? "#1e1e1e" : "white",
                        color: this.props.darkMode ? "white" : "black",
                    }}
                    disabled={this.state.SavedSuccessfully}
                    defaultSelectedKey={this.state.defaultCheckinKey}
                    options={checkinOptions}
                    onChange={(_, val) => this.setUploadOption(val)}
                    required={true}
                />
            );
        }
    }

    onExpand = (item: FilingLocation) => {
        this.setState({browsingUrl: item.Url});
    };

    setMetadataValueString = (Value: string, field: string) => {
        this.props.teamDocumentsContext.setParentState({
            metadataFields: this.props.teamDocumentsContext.metadataFields.map((el) =>
                el.Field === field ? Object.assign({}, el, {Value: Value, ErrorMessage: ""}) : el
            ),
        });
    };

    setMetadataValueNumber = (Value: string, field: string) => {
        const reg = new RegExp("(^[0-9]+$|^$)");

        const onlyNumbers = reg.test(Value);

        if (onlyNumbers) {
            this.props.teamDocumentsContext.setParentState({
                metadataFields: this.props.teamDocumentsContext.metadataFields.map((el) =>
                    el.Field === field ? Object.assign({}, el, {Value: Value, ErrorMessage: ""}) : el
                ),
            });
        }
    };

    getDate = (Value: Date) => {
        let day = Value.getDate().toString();
        if (day.length < 2) {
            day = "0" + day;
        }

        const month = Value.getMonth() + 1;
        let stringMonth = month.toString();
        if (stringMonth.length < 2) {
            stringMonth = "0" + stringMonth;
        }

        const newDate = Value.getFullYear() + "-" + stringMonth + "-" + day + "T00:00:00.000Z";
        return newDate;
    };

    setMetadataValueDate = (Value: Date, field: string) => {
        const newDate = this.getDate(Value);

        this.props.teamDocumentsContext.setParentState({
            metadataFields: this.props.teamDocumentsContext.metadataFields.map((el) =>
                el.Field === field ? Object.assign({}, el, {Value: newDate, ErrorMessage: ""}) : el
            ),
        });
    };

    reorderMetadataValues = (metadataField: MetadataFields, direction: boolean) => {
        const metadataList = this.props.teamDocumentsContext.metadataFields;

        const orderList = this.props.teamDocumentsContext.metadataOrder;
        // add metadata ids to the order list if they don't exist there
        // since they will always be at the end of the metadata list, we can add them to the end of the metadata order
        metadataList.forEach((metadata) => {
            if (!orderList.includes(metadata.Id)) {
                orderList.push(metadata.Id);
            }
        });

        const fieldIndex = metadataList.indexOf(metadataField);

        const orderIndex = orderList.findIndex((i) => i == metadataField.Id);

        let toIndex: number;

        if (direction) {
            // move the field up the list
            toIndex = fieldIndex - 1;
            if (toIndex == 1) {
                // always keep client and matter at the top?
                return;
            }
        } else {
            // move the field down the list
            toIndex = fieldIndex + 1;
        }

        const element = metadataList.splice(fieldIndex, 1)[0];
        metadataList.splice(toIndex, 0, element);

        const element2 = orderList.splice(orderIndex, 1)[0];
        orderList.splice(toIndex, 0, element2);

        this.props.teamDocumentsContext.setParentState({metadataFields: metadataList, metadataOrder: orderList});
    };

    showMetadataFields() {
        if (this.props.teamDocumentsContext.metadataFields == null || this.props.isLitigationHoldEnabled) {
            return;
        }

        return this.props.teamDocumentsContext.metadataFields.map(this.createMetadataNode);
    }

    createMetadataNode = (metadata: MetadataFields) => {
        const metadataIndex = this.props.teamDocumentsContext.metadataFields.findIndex((i) => i == metadata);
        const lastIndex = this.props.teamDocumentsContext.metadataFields.length - 1;

        switch (metadata.Type) {
            case MetadataTypes.Text:
                return this.createTextMetadataNode(
                    metadata,
                    metadataIndex,
                    lastIndex,
                    false,
                    false,
                    this.setMetadataValueString
                );
            case MetadataTypes.Note:
                return this.createTextMetadataNode(
                    metadata,
                    metadataIndex,
                    lastIndex,
                    !metadata.ManuallyDisabled,
                    true,
                    this.setMetadataValueString
                );
            case MetadataTypes.Number:
                return this.createTextMetadataNode(
                    metadata,
                    metadataIndex,
                    lastIndex,
                    false,
                    false,
                    this.setMetadataValueNumber
                );
            case MetadataTypes.Date:
                return this.createDateMetadataNode(metadata, metadataIndex, lastIndex);
            case MetadataTypes.Search:
                return this.createSearchMetadataNode(metadata, metadataIndex, lastIndex);
            case MetadataTypes.Lookup:
                return this.createLookupMetadataNode(metadata);
        }
    };

    private createTextMetadataNode(
        metadata: MetadataFields,
        metadataIndex: number,
        lastIndex: number,
        isResizeable: boolean,
        isMultiline: boolean,
        setValueCallback: (value: string, field: string) => void
    ): ReactNode {
        return (
            <TeamsDocumentStatusTabTextMetadata
                metadata={metadata}
                metadataListIndex={metadataIndex}
                metadataLastListIndex={lastIndex}
                shouldReorderMetadata={this.state.reorderMetadata}
                isDarkMode={this.props.darkMode}
                isResizeable={isResizeable}
                isMultiline={isMultiline}
                reorderCallback={this.reorderMetadataValues}
                setListValueCallback={setValueCallback}
            />
        );
    }

    private createDateMetadataNode(metadata: MetadataFields, metadataIndex: number, lastIndex: number): ReactNode {
        return (
            <TeamsDocumentStatusTabDateMetadata
                metadata={metadata}
                metadataListIndex={metadataIndex}
                metadataLastListIndex={lastIndex}
                validationErrors={this.state.validationErrors}
                shouldReorderMetadata={this.state.reorderMetadata}
                isDarkMode={this.props.darkMode}
                reorderCallback={this.reorderMetadataValues}
                setListDateCallback={this.setMetadataValueDate}
            />
        );
    }

    private createSearchMetadataNode(metadata: MetadataFields, metadataIndex: number, lastIndex: number): ReactNode {
        return (
            <TeamsDocumentStatusTabSearchMetadata
                metadata={metadata}
                metadataListIndex={metadataIndex}
                metadataLastListIndex={lastIndex}
                shouldReorderMetadata={this.state.reorderMetadata}
                isDarkMode={this.props.darkMode}
                reorderCallback={this.reorderMetadataValues}
                clearIfEmptyCallback={this.clearDropdownOptionsIfEmpty}
                runSearchCallback={this.runSearchForMetadata}
                searchingProfileAttribute={this.state.searchingProfileAttribute}
                setSearchFilterCallback={this.setSearchFilter}
            />
        );
    }

    private createLookupMetadataNode(metadata: MetadataFields): ReactNode {
        return (
            <TeamsDocumentStatusTabLookupMetadata
                metadata={metadata}
                isDarkMode={this.props.darkMode}
                setSearchFilterCallback={this.setSearchFilter}
            />
        );
    }

    resetLinkedFieldIfParentEmpty = (metadata: MetadataFields) => {
        if (metadata.Linked != 0) {
            return; // if the field thats been dismissed is a linked field, there won't be any other field to reset
        }

        const items = this.props.teamDocumentsContext.metadataFields;

        if (metadata.DropDownOptions.length == 0) {
            const item = items.filter((i) => i.Linked.toString() == metadata.Id.toString());

            if (item.length == 1) {
                item[0].DropDownOptions = [];
                item[0].Disabled = true;

                this.props.teamDocumentsContext.setParentState({metadataFields: items});
            }
        }
    };

    clearDropdownOptionsIfEmpty = (metadata: MetadataFields) => {
        if (metadata.HasAllOptions) {
            return;
        }

        const items = this.props.teamDocumentsContext.metadataFields;

        const item = items.filter((i) => i == metadata);

        const dropDownValue = item[0].DropDownOptions.filter((i) => i.selected);

        if (dropDownValue.length == 0 && metadata.Linked == 0) {
            // If there's no selected value and no linked value. We don't want to clear linked options as we specifically request them after the parent value is set
            item[0].DropDownOptions = [];
            this.props.teamDocumentsContext.setParentState({metadataFields: items});
            this.setState(() => {
                this.resetLinkedFieldIfParentEmpty(metadata);
            });
        }
    };

    setSearchFilter = (val: IDropdownOption, field: string, id: string, defaultList: DefaultValues[]) => {
        if (!val) {
            return;
        }

        const items = this.props.teamDocumentsContext.metadataFields;

        const item = items.filter((i) => i.Field == field);

        const selectedOption = item[0].DropDownOptions.filter((i) => i.key == val.key);

        if (selectedOption.length != 0) {
            const objIndex = item[0].DropDownOptions.findIndex((obj) => obj == val);

            item[0].DropDownOptions[objIndex] = selectedOption[0];

            item[0].ErrorMessage = "";

            for (let i = 0; i < item[0].DropDownOptions.length; i++) {
                item[0].DropDownOptions[i].selected = false;
            }

            selectedOption[0].selected = true;
            this.props.teamDocumentsContext.setParentState({metadataFields: items});
            this.setState(() => {
                this.getOtherOptions(id, val, defaultList);
            });
        }
    };

    getLinkedFieldsValues = async (attributeId: string, parentKey: string | number) => {
        let dropDownOptions: IDropdownOption[];

        // Calling this endpoint without a search string will return the first 50 values
        await post("/api/teamsdmsintegration/MetadataLookupSearch", {
            TenantId: this.state.tenantId,
            TeamsId: this.state.teamsId,
            CaseDetails: this.props.CaseInfo.CaseDetails,
            StorageType: this.props.CaseInfo.StorageType,
            SearchString: "",
            AttributeId: attributeId,
            ParentKey: parentKey,
            UserSearch: false,
        })
            .then((response) => response.json())
            .then((response) => {
                const lookupResponse = response as MetadataLookupSearchResponse;

                dropDownOptions = lookupResponse.DropDownOptions;
            });

        return dropDownOptions;
    };

    getOtherOptions = async (linkedId: string, parentValue: IDropdownOption, defaultList: DefaultValues[]) => {
        const items = this.props.teamDocumentsContext.metadataFields;

        const linkedFields = items.filter((i) => i.Linked.toString() == linkedId);

        for (const linkedField of linkedFields) {
            linkedField.Disabled = false;
            linkedField.ParentKey = parentValue.key.toString();
            linkedField.DropDownOptions = await this.getLinkedFieldsValues(linkedField.Id, parentValue.key);
            linkedField.Value = "";
        }

        const defaultFields = items.filter((i) => i.DefaultSource.toString() == linkedId);

        if (defaultList != null) {
            for (const option of defaultFields) {
                const defaultValueToUse = defaultList.filter((i) => i.Key == parentValue.key)[0].DefaultValue;
                option.Value = defaultValueToUse;
            }
        }

        this.props.teamDocumentsContext.setParentState({metadataFields: items});
    };

    enableMetadataReorder() {
        const metadata = this.props.teamDocumentsContext.metadataFields;

        if (this.state.reorderMetadata) {
            for (const option of metadata) {
                option.ManuallyDisabled = false;
            }
            this.setState({reorderMetadata: false});
            this.props.teamDocumentsContext.setParentState({metadataFields: metadata});
        } else {
            for (const option of metadata) {
                option.ManuallyDisabled = true;
            }
            this.setState({reorderMetadata: true});
        }
    }

    changeChannelOption(newChannel: string, data: {icon: string}) {
        if (this.props.teamDocumentsContext.channelId != newChannel) {
            this.setState({
                changingChannel: true,
                checkingOutToPrivateChannel: false,
                showCheckingOutToPrivateChannelBanner: false,
            });
            this.props.teamDocumentsContext.changeTeamsChannel(newChannel, data).then(() =>
                this.setState({
                    changingChannel: false,
                })
            );
            if (data && data.icon == "Lock") {
                this.setState({showCheckingOutToPrivateChannelBanner: true, checkingOutToPrivateChannel: true});
            }
        }
    }

    folderSelected = (value: boolean, location: FilingLocation) => {
        this.setState({showShareButton: value, selectedFolder: location});
    };

    async shareDocument() {
        this.setState({sharingDocument: true});

        const shareFolderInfo: CheckoutFolderInfo = {
            FolderId: this.state.selectedFolder.Metadata["CheckoutFolderId"],
            FolderUrl: this.state.selectedFolder.Url,
            ChannelId: this.state.selectedFolder.Metadata["ChannelId"],
        };

        await post("/api/teamsdmsintegration/ShareDocumentToExternalTeam", {
            Metadata: this.state.CheckinDocumentInfo.Metadata,
            TeamsId: this.state.teamsId,
            ChannelId: this.props.teamDocumentsContext.channelId,
            TenantId: this.state.tenantId,
            ExternalTeamId: this.state.selectedFolder.ExternalTeamId,
            ExternalChannelId: this.state.selectedFolder.ExternalChannelId,
            FileNeedsUnlocked: this.state.NoLocalChanges,
            CheckoutFolderInfo: shareFolderInfo,
        }).then((r) => {
            if (r.status.toString().startsWith("2")) {
                this.setState({shareSuccessful: true, sharingDocument: false, showBanner: true});
            } else {
                this.setState({shareFailed: true, sharingDocument: false, showBanner: true});
            }
        });

        this.refreshTeamsDocumentsPanel();
    }

    public render() {
        const storageTypeLabel = TeamsTab.getStorageTypeLabel(this.props.CaseInfo.StorageType);

        const checkinPanelText = this.props.isLitigationHoldEnabled
            ? "Save to " + storageTypeLabel
            : this.props.isFrictionlessSpExperienceEnabled
            ? "Move to " + storageTypeLabel
            : "Check In to " + storageTypeLabel;
            
        const uploadPanelText = this.props.isFrictionlessSpExperienceEnabled
            ? "Move to " + storageTypeLabel
            : "Upload to "  + storageTypeLabel;

        let checkinPanelMargin = this.props.isLitigationHoldEnabled
            ? "144px"
            : this.props.teamDocumentsContext.isTeamOwner
            ? "35px"
            : "110px";

        let uploadPanelMargin = this.props.isLitigationHoldEnabled
            ? "122px"
            : this.props.teamDocumentsContext.isTeamOwner
            ? "50px"
            : "123px";

        const sharePanelMargin =
            this.props.teamDocumentsContext.isTeamOwner &&
            this.props.teamDocumentsContext.metadataEnabled &&
            !this.state.NoLocalChanges
                ? "152px"
                : "230px";

        if (!this.props.teamDocumentsContext.metadataEnabled) {
            if (this.props.CaseInfo.StorageType == DmsStorageType.NetDocuments) {
                checkinPanelMargin = "110px";
                uploadPanelMargin = "123px";
            } else if (this.props.CaseInfo.StorageType == DmsStorageType.iManage) {
                checkinPanelMargin = "151px";
                uploadPanelMargin = "164px";
            } else if (this.props.CaseInfo.StorageType == DmsStorageType.SharePoint) {
                if(this.props.isFrictionlessSpExperienceEnabled){
                    checkinPanelMargin = "180px";
                    uploadPanelMargin = "180px";
                }
                else{
                    checkinPanelMargin = "148px";
                    uploadPanelMargin = "161px";
                }                
            }
        }

        let iconDetails: string[] = [];

        if (this.state.showCheckinPanel || this.state.showSharePanel || this.state.showUploadNewDocumentPanel) {
            iconDetails = GetIcons.getIconName(this.state.CheckinDocumentInfo.DocName, this.props.darkMode, this.state.CheckinDocumentInfo.Metadata, true);
        }

        if (this.props.teamDocumentsContext.documentsErrorMessage) {
            return (
                <div>
                    <p style={{color: "red"}}>{this.props.teamDocumentsContext.documentsErrorMessage}</p>
                </div>
            );
        } else if (
            this.props.teamDocumentsContext.rootNetworkState == NetworkState.Loading ||
            this.props.teamDocumentsContext.litigationHoldNetworkState == NetworkState.Loading
        ) {
            return (
                <div>
                    {" "}
                    <Spinner
                        className={this.props.darkMode ? "darkMode" : "lightMode"}
                        label="Loading contents..."
                    />{" "}
                </div>
            );
        } else {
            return (
                <div style={{paddingTop: "5px", height: "calc(100% - 5px)"}}>
                    {this.state.showCheckinPanel && this.state.CheckinDocumentInfo.Metadata["InDMS"] == "Yes" && (
                        <Panel // panel; for checking in existing documents to the DMS
                            isOpen={this.state.showCheckinPanel}
                            className={this.props.darkMode ? "darkMode" : "lightMode"}
                            onDismiss={() => this.resetMetadata()}
                            closeButtonAriaLabel="Close"
                            customWidth="425px"
                            type={PanelType.custom}
                            onRenderHeader={() => this.handleRenderHeader(checkinPanelText, checkinPanelMargin)}
                        >
                            <hr style={{height: "10px", visibility: "hidden"}} />

                            {this.state.checkingOutToPrivateChannel &&
                                this.state.showCheckingOutToPrivateChannelBanner && (
                                    <MessageBar
                                        onDismiss={() => this.setState({showCheckingOutToPrivateChannelBanner: false})}
                                        isMultiline={true}
                                        messageBarType={MessageBarType.warning}
                                    >
                                        <div style={{fontSize: "14px"}}>
                                            {
                                                "Security in the document storage area for this document may differ from the Private Channel."
                                            }
                                        </div>
                                    </MessageBar>
                                )}
                            {this.state.checkingOutToPrivateChannel &&
                                this.state.showCheckingOutToPrivateChannelBanner && (
                                    <hr style={{height: "10px", visibility: "hidden"}} />
                                )}

                            {(this.state.LoadingExistingMetadata ||
                                this.props.teamDocumentsContext.metadataNetworkState != NetworkState.Success) && (
                                <Spinner />
                            )}

                            {!this.state.LoadingExistingMetadata &&
                                this.props.teamDocumentsContext.metadataNetworkState == NetworkState.Success && (
                                    <div>
                                        {(this.state.SavedSuccessfully ||
                                            this.state.SaveFailed ||
                                            this.state.NoLocalChanges) &&
                                            this.state.showBanner && (
                                                <TeamsDocumentStatusTabBanner
                                                    isSuccess={this.state.SavedSuccessfully}
                                                    isError={this.state.SaveFailed}
                                                    bannerMessage={this.getBannerMsgForCheckin()}
                                                    onDismiss={
                                                        this.state.SavedSuccessfully
                                                            ? null
                                                            : () =>
                                                                  this.setState({
                                                                      showBanner: false,
                                                                      SaveFailedMessage: {message: null, error: null},
                                                                      NoLocalChanges: false,
                                                                  })
                                                    }
                                                />
                                            )}
                                        {(this.state.SavedSuccessfully ||
                                            this.state.SaveFailed ||
                                            this.state.NoLocalChanges) && (
                                            <hr style={{height: "10px", visibility: "hidden"}} />
                                        )}
                                        {(this.state.RemoveFileState == NetworkState.Error ||
                                            this.state.RemoveFileState == NetworkState.Success) &&
                                            this.state.showRemoveBanner && (
                                                <TeamsDocumentStatusTabBanner
                                                    isSuccess={this.state.RemoveFileState == NetworkState.Success}
                                                    isError={this.state.RemoveFileState == NetworkState.Error}
                                                    bannerMessage={this.getBannerMsgForRemove()}
                                                    onDismiss={
                                                        this.state.RemoveFileState == NetworkState.Success
                                                            ? null
                                                            : () => this.setState({showRemoveBanner: false})
                                                    }
                                                />
                                            )}
                                        {(this.state.RemoveFileState == NetworkState.Error ||
                                            this.state.RemoveFileState == NetworkState.Success) && (
                                            <hr style={{height: "10px", visibility: "hidden"}} />
                                        )}
                                        {!this.props.isFrictionlessSpExperienceEnabled &&
                                        (this.state.SaveFailedMessage.error == "DocumentMissingCheckoutException" ||
                                            this.state.SaveFailedMessage.error == "AlreadyUploadedException") && (
                                            <div>
                                                {this.state.RemoveFileState != NetworkState.Success && (
                                                    <Checkbox
                                                        disabled={this.state.SavedSuccessfully}
                                                        checked={this.state.KeepFileCheckedOut}
                                                        onChange={(_, val) => this.setState({KeepFileCheckedOut: val})}
                                                        className={this.props.darkMode ? "darkMode" : "lightMode"}
                                                        label="Keep checked out to Teams?"
                                                    />
                                                )}

                                                <br></br>

                                                {!this.state.SavingFile &&
                                                    this.state.RemoveFileState != NetworkState.Success && (
                                                        <PrimaryButton
                                                            disabled={this.state.SavedSuccessfully}
                                                            onClick={(_) =>
                                                                this.saveFileToDms(
                                                                    {
                                                                        Metadata: {
                                                                            ...this.state.CheckinDocumentInfo.Metadata,
                                                                            InDMS: "No",
                                                                            DmsFileId: null,
                                                                            DocumentNumber: "",
                                                                            UseDmsFileId: "",
                                                                        },
                                                                        UseDmsFileId: true,
                                                                    },
                                                                    true
                                                                )
                                                            }
                                                            style={{textAlign: "center"}}
                                                            text="Upload as new file"
                                                        />
                                                    )}
                                                {!this.state.SavingFile &&
                                                    this.state.RemoveFileState != NetworkState.Success && (
                                                        <PrimaryButton
                                                            disabled={
                                                                this.state.SavedSuccessfully ||
                                                                this.state.RemoveFileState == NetworkState.Loading
                                                            }
                                                            onClick={(_) => this.removeTeamsFile()}
                                                            style={{textAlign: "center", marginLeft: 15}}
                                                            text="Delete from Teams"
                                                        />
                                                    )}
                                                {(this.state.SavingFile ||
                                                    this.state.RemoveFileState == NetworkState.Loading) && <Spinner />}
                                            </div>
                                        )}
                                        {this.state.SaveFailedMessage.error != "DocumentMissingCheckoutException" &&
                                            this.state.SaveFailedMessage.error != "AlreadyUploadedException" && (
                                                <div>
                                                    <div className="metadataWrapper">
                                                        <div className="checkoutIcon">
                                                            <Icon
                                                                iconName={iconDetails[0]}
                                                                style={{
                                                                    fontSize: "24px",
                                                                    marginTop: "6px",
                                                                    color: iconDetails[1],
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="checkoutFileName preWrap">
                                                            <p>{this.state.CheckinDocumentInfo.DocName}</p>
                                                        </div>
                                                    </div>

                                                    {this.showMetadataFields()}

                                                    <hr style={{height: "10px", visibility: "hidden"}} />
                                                    <TextField
                                                        disabled={this.state.SavedSuccessfully}
                                                        multiline={true}
                                                        value={this.state.CheckinComment}
                                                        onChange={(_, val) => this.setState({CheckinComment: val})}
                                                        placeholder={
                                                            this.props.isLitigationHoldEnabled
                                                                ? "Comment (optional)"
                                                                : "Check in comment (optional)"
                                                        }
                                                    />

                                                    {!this.props.isLitigationHoldEnabled && this.getCheckinOptions()}

                                                    <hr style={{height: "10px", visibility: "hidden"}} />
                                                    {!this.props.isFrictionlessSpExperienceEnabled &&
                                                    (
                                                        <div>
                                                            <Checkbox
                                                                disabled={this.state.SavedSuccessfully}
                                                                checked={this.state.KeepFileCheckedOut}
                                                                onChange={(_, val) =>
                                                                    this.setState({KeepFileCheckedOut: val})
                                                                }
                                                                className={
                                                                    this.props.darkMode ? "darkMode" : "lightMode"
                                                                }
                                                                label={
                                                                    this.props.isLitigationHoldEnabled
                                                                        ? "Keep file in Teams?"
                                                                        : "Keep checked out to Teams?"
                                                                }
                                                            />
                                                            <br></br>
                                                        </div>
                                                    )}
                                                    {!this.state.SavingFile && (
                                                        <PrimaryButton
                                                            disabled={
                                                                this.state.SavedSuccessfully ||
                                                                this.state.CheckinDocumentInfo.IsUploading
                                                            }
                                                            onClick={(_) => this.saveFileToDms()}
                                                            style={{textAlign: "center"}}
                                                            text={
                                                                this.props.isLitigationHoldEnabled 
                                                                ? "Save" 
                                                                : this.props.isFrictionlessSpExperienceEnabled 
                                                                ? "Move"
                                                                : "Check In"
                                                            }
                                                        />
                                                    )}
                                                    {this.state.SavingFile && <Spinner />}
                                                </div>
                                            )}
                                    </div>
                                )}
                        </Panel>
                    )}

                    {this.state.showCheckinPanel && this.state.CheckinDocumentInfo.Metadata["InDMS"] == "No" && (
                        <Panel // panel for uploading new documents to the DMS
                            isOpen={this.state.showCheckinPanel}
                            className={this.props.darkMode ? "darkMode" : "lightMode"}
                            onDismiss={() => this.resetMetadata()}
                            closeButtonAriaLabel="Close"
                            customWidth="425px"
                            type={PanelType.custom}
                            onRenderHeader={() => this.handleRenderHeader(uploadPanelText, uploadPanelMargin)}
                        >
                            <hr style={{height: "10px", visibility: "hidden"}} />

                            {this.state.checkingOutToPrivateChannel &&
                                this.state.showCheckingOutToPrivateChannelBanner && (
                                    <MessageBar
                                        onDismiss={() => this.setState({showCheckingOutToPrivateChannelBanner: false})}
                                        isMultiline={true}
                                        messageBarType={MessageBarType.warning}
                                    >
                                        <div style={{fontSize: "14px"}}>
                                            {
                                                "Security in the document storage area for this document may differ from the Private Channel."
                                            }
                                        </div>
                                    </MessageBar>
                                )}
                            {this.state.checkingOutToPrivateChannel &&
                                this.state.showCheckingOutToPrivateChannelBanner && (
                                    <hr style={{height: "10px", visibility: "hidden"}} />
                                )}

                            {this.props.teamDocumentsContext.metadataNetworkState != NetworkState.Success && (
                                <Spinner />
                            )}

                            {this.props.teamDocumentsContext.metadataNetworkState == NetworkState.Success && (
                                <div>
                                    {(this.state.SavedSuccessfully || this.state.SaveFailed) &&
                                        this.state.showBanner && (
                                            <TeamsDocumentStatusTabBanner
                                                isSuccess={this.state.SavedSuccessfully}
                                                isError={this.state.SaveFailed}
                                                bannerMessage={this.getBannerMsgForUpload()}
                                                onDismiss={
                                                    this.state.SavedSuccessfully
                                                        ? null
                                                        : () => this.setState({showBanner: false})
                                                }
                                            />
                                        )}

                                    {(this.state.SavedSuccessfully || this.state.SaveFailed) && (
                                        <hr style={{height: "10px", visibility: "hidden"}} />
                                    )}
                                    <div className="metadataWrapper">
                                        <div className="checkoutIcon">
                                            <Icon
                                                iconName={iconDetails[0]}
                                                style={{fontSize: "24px", marginTop: "6px", color: iconDetails[1]}}
                                            />
                                        </div>
                                        <div className="checkoutFileName preWrap">
                                            <p>{this.state.CheckinDocumentInfo.DocName}</p>
                                        </div>
                                    </div>
                                    {this.showMetadataFields()}
                                    <br></br>
                                    <TextField
                                        disabled={this.state.SavedSuccessfully}
                                        multiline={true}
                                        value={this.state.CheckinComment}
                                        onChange={(_, val) => this.setState({CheckinComment: val})}
                                        placeholder={
                                            this.props.isLitigationHoldEnabled
                                                ? "Comment (optional)"
                                                : "Check in comment (optional)"
                                        }
                                    />
                                    <hr style={{height: "10px", visibility: "hidden"}} />
                                    {!this.props.isFrictionlessSpExperienceEnabled &&
                                    (
                                        <Checkbox
                                            disabled={this.state.SavedSuccessfully}
                                            checked={this.state.KeepFileCheckedOut}
                                            onChange={(_, val) => this.setState({KeepFileCheckedOut: val})}
                                            className={this.props.darkMode ? "darkMode" : "lightMode"}
                                            label={
                                                this.props.isLitigationHoldEnabled
                                                    ? "Keep file in Teams?"
                                                    : "Keep checked out to Teams?"
                                            }
                                        />
                                    )}
                                    <br></br>
                                    {!this.state.SavingFile && (
                                        <PrimaryButton
                                            disabled={
                                                this.state.SavedSuccessfully ||
                                                this.state.CheckinDocumentInfo.IsUploading
                                            }
                                            onClick={(_) => this.saveFileToDms()}
                                            style={{textAlign: "center"}}
                                            text={
                                                this.props.isFrictionlessSpExperienceEnabled 
                                                ? "Move"
                                                : "Upload"
                                            }
                                        />
                                    )}

                                    {this.state.SavingFile && <Spinner />}
                                </div>
                            )}
                        </Panel>
                    )}

                    {this.state.showUploadNewDocumentPanel && (
                        <Panel // panel for creating new document for a checked out file
                            isOpen={this.state.showUploadNewDocumentPanel}
                            className={this.props.darkMode ? "darkMode" : "lightMode"}
                            onDismiss={() => this.setState({showUploadNewDocumentPanel: false})}
                            closeButtonAriaLabel="Close"
                            customWidth="425px"
                            type={PanelType.custom}
                            onRenderHeader={() => this.handleRenderHeader(uploadPanelText, uploadPanelMargin)}
                        >
                            <hr style={{height: "10px", visibility: "hidden"}} />

                            {this.props.teamDocumentsContext.metadataNetworkState != NetworkState.Success && (
                                <Spinner />
                            )}

                            {this.props.teamDocumentsContext.metadataNetworkState == NetworkState.Success && (
                                <div>
                                    {(this.state.SavedSuccessfully || this.state.SaveFailed) &&
                                        this.state.showBanner && (
                                            <TeamsDocumentStatusTabBanner
                                                isSuccess={this.state.SavedSuccessfully}
                                                isError={this.state.SaveFailed}
                                                bannerMessage={this.getBannerMsgForUpload()}
                                                onDismiss={
                                                    this.state.SavedSuccessfully
                                                        ? null
                                                        : () => this.setState({showBanner: false})
                                                }
                                            />
                                        )}

                                    {(this.state.SavedSuccessfully || this.state.SaveFailed) && (
                                        <hr style={{height: "10px", visibility: "hidden"}} />
                                    )}
                                    <div className="metadataWrapper">
                                        <div className="checkoutIcon">
                                            <Icon
                                                iconName={iconDetails[0]}
                                                style={{fontSize: "24px", marginTop: "6px", color: iconDetails[1]}}
                                            />
                                        </div>
                                        <div className="checkoutFileName preWrap">
                                            <p>{this.state.CheckinDocumentInfo.DocName}</p>
                                        </div>
                                    </div>
                                    <br></br>

                                    <div>
                                        <NewFileNameTextField
                                            isDisabled={this.state.SavedSuccessfully || this.state.SavingFile}
                                            initialFileName={this.state.CheckinDocumentInfo.DocName}
                                            onErrorCheck={(isError) => this.setState({isNewFileNameError: isError})}
                                            onFileNameChange={(fileName) => this.setState({newFileName: fileName})}
                                        />
                                        <br />
                                    </div>

                                    <TextField
                                        disabled={this.state.SavedSuccessfully || this.state.SavingFile}
                                        multiline={true}
                                        value={this.state.CheckinComment}
                                        onChange={(_, val) => this.setState({CheckinComment: val})}
                                        placeholder="Check in comment (optional)"
                                    />
                                    <br></br>
                                    {!this.state.SavingFile && (
                                        <PrimaryButton
                                            disabled={
                                                this.state.SavedSuccessfully ||
                                                this.state.CheckinDocumentInfo.IsUploading ||
                                                this.state.isNewFileNameError
                                            }
                                            onClick={(_) => this.uploadNewDocument()}
                                            style={{textAlign: "center"}}
                                            text="Upload as new file"
                                        />
                                    )}

                                    {this.state.SavingFile && <Spinner />}
                                </div>
                            )}
                        </Panel>
                    )}

                    {this.state.showSharePanel && this.state.CheckinDocumentInfo.Metadata["InDMS"] == "Yes" && (
                        <Panel // panel for checking in documents and sharing to an external team
                            isOpen={this.state.showSharePanel}
                            className={this.props.darkMode ? "darkMode" : "lightMode"}
                            onDismiss={() => this.resetMetadata()}
                            closeButtonAriaLabel="Close"
                            customWidth="450px"
                            type={PanelType.custom}
                            onRenderHeader={() => this.handleRenderHeader("Share document", sharePanelMargin)}
                        >
                            {(this.state.LoadingExistingMetadata ||
                                this.props.teamDocumentsContext.metadataNetworkState != NetworkState.Success) && (
                                <Spinner />
                            )}
                            {!this.state.LoadingExistingMetadata &&
                                this.props.teamDocumentsContext.metadataNetworkState == NetworkState.Success && (
                                    <div>
                                        {!this.state.NoLocalChanges &&
                                            !this.state.shareSuccessful &&
                                            this.state.showBanner && (
                                                <TeamsDocumentStatusTabBanner
                                                    isSuccess={this.state.SavedSuccessfully}
                                                    isError={!this.state.SavedSuccessfully}
                                                    bannerMessage={
                                                        this.state.SavedSuccessfully
                                                            ? "Checked in changes"
                                                            : "Recent changes must be checked in before the file can be shared."
                                                    }
                                                    onDismiss={() => this.setState({showBanner: false})}
                                                />
                                            )}

                                        {this.state.SaveFailed && this.state.showCheckInBannerWhenSharing && (
                                            <div>
                                                <hr style={{height: "10px", visibility: "hidden"}} />
                                                <TeamsDocumentStatusTabBanner
                                                    isSuccess={this.state.SavedSuccessfully}
                                                    isError={this.state.SaveFailed}
                                                    bannerMessage={this.getBannerMsgForUpload()}
                                                    onDismiss={
                                                        this.state.SavedSuccessfully
                                                            ? null
                                                            : () => this.setState({showCheckInBannerWhenSharing: false})
                                                    }
                                                />
                                            </div>
                                        )}

                                        {(this.state.shareSuccessful || this.state.shareFailed) &&
                                            this.state.showBanner && (
                                                <TeamsDocumentStatusTabBanner
                                                    isSuccess={this.state.shareSuccessful}
                                                    isError={!this.state.shareSuccessful}
                                                    bannerMessage={
                                                        this.state.shareSuccessful
                                                            ? "File shared successfully"
                                                            : "Failed to share file"
                                                    }
                                                    onDismiss={
                                                        this.state.shareSuccessful
                                                            ? null
                                                            : () => this.setState({showBanner: false})
                                                    }
                                                />
                                            )}

                                        {(this.state.SavedSuccessfully ||
                                            this.state.SaveFailed ||
                                            this.state.NoLocalChanges) && (
                                            <hr style={{height: "10px", visibility: "hidden"}} />
                                        )}

                                        {!this.state.SavedSuccessfully && !this.state.NoLocalChanges && (
                                            <div>
                                                <div>
                                                    <div className="metadataWrapper">
                                                        <div className="checkoutIcon">
                                                            <Icon
                                                                iconName={iconDetails[0]}
                                                                style={{
                                                                    fontSize: "24px",
                                                                    marginTop: "6px",
                                                                    color: iconDetails[1],
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="checkoutFileName preWrap">
                                                            <p>{this.state.CheckinDocumentInfo.DocName}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                {this.showMetadataFields()}
                                                <hr style={{height: "10px", visibility: "hidden"}} />
                                                <TextField
                                                    disabled={this.state.SavedSuccessfully}
                                                    multiline={true}
                                                    value={this.state.CheckinComment}
                                                    onChange={(_, val) => this.setState({CheckinComment: val})}
                                                    placeholder="Check in comment (optional)"
                                                />
                                                {this.getCheckinOptions()}
                                                <br></br>
                                                <p>The file will be removed from Teams after it is checked in.</p>
                                                <br></br>
                                                {!this.state.SavingFile && (
                                                    <PrimaryButton
                                                        disabled={this.state.SavedSuccessfully}
                                                        onClick={(_) => this.saveFileToDms()}
                                                        style={{textAlign: "center"}}
                                                        text= {
                                                            this.props.isFrictionlessSpExperienceEnabled 
                                                            ? "Move to SharePoint"
                                                            : "Check In"
                                                        }
                                                    />
                                                )}
                                                {this.state.SavingFile && <Spinner />}
                                            </div>
                                        )}

                                        {(this.state.SavedSuccessfully || this.state.NoLocalChanges) && (
                                            <div>
                                                <div className="metadataWrapper">
                                                    <div className="checkoutIcon">
                                                        <Icon
                                                            iconName={iconDetails[0]}
                                                            style={{
                                                                fontSize: "24px",
                                                                marginTop: "6px",
                                                                color: iconDetails[1],
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="checkoutFileName preWrap">
                                                        <p>{this.state.CheckinDocumentInfo.DocName}</p>
                                                    </div>
                                                </div>
                                                <hr style={{height: "10px", visibility: "hidden"}} />

                                                {!this.state.shareSuccessful && (
                                                    <ExternalTeamChannelsSharing
                                                        externalTeamName={this.props.externalTeamName}
                                                        externalTeamNameRetrievalState={
                                                            this.props.externalTeamNameRetrievalState
                                                        }
                                                        externalChannels={this.props.externalTeamChannels}
                                                        externalChannelsRetrievalState={
                                                            this.props.externalChannelsRetrievalState
                                                        }
                                                        channelId={
                                                            this.props.externalTeamChannels != null
                                                                ? this.props.externalTeamChannels.Page[0]
                                                                      .ExternalChannelId
                                                                : this.props.teamDocumentsContext.channelId
                                                        }
                                                        teamsId={this.state.teamsId}
                                                        tenantId={this.state.tenantId}
                                                        userId={this.state.userId}
                                                        storageType={this.props.CaseInfo.StorageType}
                                                        searchFilter={null}
                                                        isClientCollabLicensed={this.props.clientCollabLicensed}
                                                        isDarkMode={this.props.darkMode}
                                                        onChooseLocation={null}
                                                        showFilingButton={this.folderSelected}
                                                    />
                                                )}

                                                {this.state.showShareButton && !this.state.shareSuccessful && (
                                                    <p>
                                                        The file will be checked out to the linked workspace for Client
                                                        Collaboration
                                                    </p>
                                                )}

                                                <hr style={{height: "10px", visibility: "hidden"}} />

                                                {this.state.sharingDocument && (
                                                    <div>
                                                        <Spinner />
                                                    </div>
                                                )}
                                                {this.state.showShareButton &&
                                                    !this.props.isLitigationHoldEnabled &&
                                                    !this.state.sharingDocument &&
                                                    !this.state.shareSuccessful && (
                                                        <div>
                                                            <PrimaryButton
                                                                style={{width: "125px"}}
                                                                onClick={() => this.shareDocument()}
                                                                text={"Share"}
                                                            />
                                                        </div>
                                                    )}
                                                {this.state.shareSuccessful && (
                                                    <div>
                                                        <PrimaryButton
                                                            style={{width: "125px"}}
                                                            onClick={() => this.resetMetadata()}
                                                            text={"Ok"}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                        </Panel>
                    )}

                    {this.props.isLitigationHoldEnabled && (
                        <div style={{ marginBottom: "10px" }}>
                        <TeamsDocumentStatusTabBanner
                            isSuccess={false}
                            isError={false}
                            bannerMessage={"Litigation hold enabled for this matter. Only able to save as new version."}
                            onDismiss={null}
                        />
                        </div>
                    )}

                    {this.props.litigationHoldError && (
                        <div style={{ marginBottom: "10px" }}>
                        <TeamsDocumentStatusTabBanner
                            isSuccess={false}
                            isError={false}
                            bannerMessage={this.props.litigationHoldError}
                            onDismiss={null}
                        />
                        </div>
                    )}

                    {this.props.isLitigationHoldEnabled && <hr style={{height: "10px", visibility: "hidden"}} />}

                    {this.props.teamDocumentsContext.rootNetworkState == NetworkState.Success && (
                        <div>
                            {this.props.teamChannelsRetrievalState == ApiCallState.Loading && (
                                <Spinner label="Loading channels..." />
                            )}

                            {this.props.teamChannelsRetrievalState == ApiCallState.Success && (
                                <div style={{width: "250px"}}>
                                    <Dropdown
                                        className={this.props.darkMode ? "darkMode" : "lightMode"}
                                        styles={this.props.darkMode ? DarkModeCalloutStyles.getDropdownStyle() : null}
                                        disabled={false}
                                        selectedKey={this.props.teamDocumentsContext.channelId}
                                        placeholder="Select an option"
                                        options={this.props.teamChannels}
                                        onRenderLabel={() => this.handleRenderDropdownLabel()}
                                        onRenderOption={this.onRenderOption}
                                        onRenderTitle={this.onRenderTitle}
                                        onChange={(_, val) => this.changeChannelOption(val.key.toString(), val.data)}
                                    />
                                </div>
                            )}

                            {this.state.changingChannel && (
                                <div>
                                    <Spinner label="Loading channel contents..." />
                                </div>
                            )}

                            {!this.state.changingChannel &&
                                this.props.teamChannelsRetrievalState == ApiCallState.Success && (
                                    <div>
                                        <LocationItemsListTabView
                                            onChooseLocation={null}
                                            chooseThisLocationText={"File Here"}
                                            locations={{
                                                Page: this.props.teamDocumentsContext.documents,
                                                PagingToken: {
                                                    PagingToken: "",
                                                    Id: 0,
                                                    PageSize: null,
                                                    TotalItemCount: 0,
                                                },
                                                Errors: null,
                                            }}
                                            isSearching={this.state.isSearching}
                                            onExpand={this.onExpand}
                                            showResults={
                                                this.props.teamDocumentsContext.rootNetworkState == NetworkState.Success
                                            }
                                            searchPhrase={this.state.searchPhrase}
                                            moreResults={true}
                                            folderSearch={true}
                                            localDocs={true}
                                            channelId={this.props.teamDocumentsContext.channelId}
                                            teamsId={this.state.teamsId}
                                            storageType={this.props.CaseInfo.StorageType}
                                            userId={this.state.userId}
                                            tenantId={this.state.tenantId}
                                            errorMessage={null}
                                            TabView={this.state.TabView}
                                            ShowCheckinPanel={this.showCheckinPanel}
                                            expandedResults={this.state.expandedResults}
                                            onBack={() => {
                                                this.setState({browsingUrl: ""});
                                            }}
                                            darkMode={this.props.darkMode}
                                            searchFilter={""}
                                            checkoutToChannel={false}
                                            ShowFilingButton={null}
                                            expandedFolderResults={null}
                                            setExpandedResults={() => {}}
                                            shouldIncludeCheckedOutFiles={false}
                                            searchAllDms={false}
                                            clientCollabLicensed={this.props.clientCollabLicensed}
                                            EllipsisOptionOnClick={this.uploadNewDocOrUnlock}
                                            privateChannel={this.props.teamDocumentsContext.isPrivateChannel}
                                            isLitigationHoldEnabled={this.props.isLitigationHoldEnabled}
                                            isFrictionlessSpExperienceEnabled={this.props.isFrictionlessSpExperienceEnabled}
                                        />
                                    </div>
                                )}
                        </div>
                    )}
                </div>
            );
        }
    }

    private onRenderTitle = (options: IDropdownOption[]): JSX.Element => {
        const option = options[0];

        return (
            <div>
                <span>{option.text}</span>
                {option.data && option.data.icon && (
                    <Icon
                        style={{marginLeft: "5px"}}
                        iconName={option.data.icon}
                        aria-hidden="true"
                        title={option.data.icon}
                    />
                )}
            </div>
        );
    };

    private onRenderOption = (option: IDropdownOption): JSX.Element => {
        return (
            <div>
                <span>{option.text}</span>
                {option.data && option.data.icon && (
                    <Icon
                        style={{marginLeft: "5px"}}
                        iconName={option.data.icon}
                        aria-hidden="true"
                        title={option.data.icon}
                    />
                )}
            </div>
        );
    };

    private handleRenderHeader(headerText: string, headerPosition: string): JSX.Element {
        const overflowProps: IButtonProps = {
            style: {marginBottom: "10px"},
            menuProps: {
                items: [],
                isBeakVisible: true,
                styles: this.props.darkMode ? DarkModeCalloutStyles.getContextualMenuCalloutStyles() : null,
            },
        };

        const _overflowItems: ICommandBarItemProps[] = [
            {
                key: "move",
                text: "Edit Order",
                onClick: () => this.enableMetadataReorder(),
                iconProps: {iconName: "Edit"},
            },
        ];

        const _items: ICommandBarItemProps[] = [];

        if (
            ((this.props.teamDocumentsContext.isTeamOwner &&
                this.props.teamDocumentsContext.metadataEnabled &&
                !this.state.showSharePanel) ||
                (this.state.showSharePanel &&
                    !this.state.NoLocalChanges &&
                    this.props.teamDocumentsContext.metadataEnabled &&
                    this.props.teamDocumentsContext.isTeamOwner)) &&
            !this.props.isLitigationHoldEnabled
        ) {
            return (
                <div className="metadataWrapper">
                    <div className="metadataField2" style={{marginLeft: "25px"}}>
                        <p
                            style={{
                                fontSize: "20px",
                                fontWeight: 600,
                                color: this.props.darkMode ? "white" : "rgb(50, 49, 48)",
                                marginRight: headerPosition,
                            }}
                        >
                            {headerText}
                        </p>
                    </div>
                    <div className="metadataPositionIcons2">
                        {this.props.teamDocumentsContext.isTeamOwner && (
                            <CommandBar
                                items={_items}
                                overflowItems={_overflowItems}
                                overflowButtonProps={overflowProps}
                            />
                        )}
                    </div>
                </div>
            );
        } else {
            return (
                <p
                    style={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: this.props.darkMode ? "white" : "rgb(50, 49, 48)",
                        marginRight: headerPosition,
                    }}
                >
                    {headerText}
                </p>
            );
        }
    }

    private handleRenderDropdownLabel(): JSX.Element {
        return (
            <div>
                <p
                    style={{
                        fontSize: "18px",
                        fontWeight: 600,
                        color: this.props.darkMode ? "white" : "rgb(50, 49, 48)",
                    }}
                >
                    Choose Channel
                </p>
                <hr style={{height: "5px", visibility: "hidden"}} />
            </div>
        );
    }

    private getBannerMsgForCheckin() {
        if (this.state.SavedSuccessfully) {
            if (this.props.isLitigationHoldEnabled) {
                return "Save successful.";
            }
            if(this.props.isFrictionlessSpExperienceEnabled){
                return "Completed.";
            }
            else {
                return "Check-in successful.";
            }             
        }
        if (this.state.SaveFailed) {
            return this.state.SaveFailedMessage.message
                ? this.state.SaveFailedMessage.message
                : this.props.isLitigationHoldEnabled
                ? "Save failed."
                : this.props.isFrictionlessSpExperienceEnabled 
                ? "Move failed." 
                : "Check-in failed.";
        }
        return "No changes made.";
    }

    private getBannerMsgForRemove() {
        if (this.state.RemoveFileState == NetworkState.Success) {
            return "File removed successfully.";
        }
        if (this.state.RemoveFileState == NetworkState.Error) {
            return "Error removing file from Teams.";
        }
        return "No changes made.";
    }

    private getBannerMsgForUpload() {
        if (this.state.SavedSuccessfully) {
            if(this.props.isFrictionlessSpExperienceEnabled){
                return "Completed.";
            }
            else {
                return "Upload successful.";
            }            
        } else {
            return this.state.SaveFailedMessage.message 
            ? this.state.SaveFailedMessage.message 
            : this.props.isFrictionlessSpExperienceEnabled 
            ? "Move failed." 
            : "Upload failed.";
        }
    }
}

export const TeamsDocumentStatusTabView = WithTeamsDocumentStatusTabView(TeamsDocumentStatusTabViewComponent);
